/**
 * Zone bounds and the world -> zone-map coordinate transform.
 *
 * `zone-bounds.json` is generated by `apps/api/scripts/gen-zone-bounds.ts` from the Classic Era
 * client's UiMap tables (wago.tools). Each outdoor zone carries the world-coordinate box its
 * map covers; instances have no client map and are absent. World x grows north and world y
 * grows west, so the map's left edge is maxY and its top edge is maxX (the Wowhead convention).
 */
import bounds from './zone-bounds.json' with { type: 'json' };

export interface ZoneArt {
  width: number;
  height: number;
  tileSize: number;
  rows: number;
  cols: number;
  /** [row][col] = client FileDataID of the 256 px tile. */
  tiles: number[][];
}

/** The client's minimap tiles covering a zone's world box, `world/minimaps/<directory>/map<x>_<y>.blp`. */
export interface ZoneMinimap {
  /** `Map.Directory`, lower-case. */
  directory: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface ZoneBounds {
  areaId: number;
  uiMapId: number;
  /** The continent/instance map id spawn `locations[].map` refers to. */
  mapId: number;
  name: string;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  art?: ZoneArt;
  minimap?: ZoneMinimap;
}

export interface ZoneBoundsFixture {
  build: string;
  zones: ZoneBounds[];
  /** Map id -> FileDataID of the loading screen the client shows for that map (instances included). */
  loadingScreens: Record<string, number>;
}

export interface WorldPoint {
  x: number;
  y: number;
}

export interface SpawnLocation extends WorldPoint {
  map: number;
}

export const ZONE_BOUNDS: ZoneBoundsFixture = bounds as ZoneBoundsFixture;

const byArea = new Map(ZONE_BOUNDS.zones.map((z) => [z.areaId, z]));
const byName = new Map(ZONE_BOUNDS.zones.map((z) => [z.name, z]));
const byUiMap = new Map(ZONE_BOUNDS.zones.map((z) => [z.uiMapId, z]));

export function zoneBoundsByArea(areaId: number): ZoneBounds | undefined {
  return byArea.get(areaId);
}

export function zoneBoundsByName(name: string): ZoneBounds | undefined {
  return byName.get(name);
}

/** The zone whose client map (`C_Map.GetBestMapForUnit`) has this uiMap id. */
export function zoneBoundsByUiMap(uiMapId: number): ZoneBounds | undefined {
  return byUiMap.get(uiMapId);
}

/** One ADT (one minimap tile) in yards; the world is a 64x64 grid of them centred on tile 32. */
export const ADT_SIZE = 533.33333;

/**
 * The minimap tiles covering a world box. Tile columns run east from the west edge (largest
 * world y) and rows run south from the north edge (largest world x), so x0/y0 are the
 * north-west tile.
 */
export function minimapTileRange(box: Pick<ZoneBounds, 'minX' | 'maxX' | 'minY' | 'maxY'>): Omit<ZoneMinimap, 'directory'> {
  const tile = (v: number) => Math.floor(32 - v / ADT_SIZE);
  return { x0: tile(box.maxY), x1: tile(box.minY), y0: tile(box.maxX), y1: tile(box.minX) };
}

const round1 = (v: number) => Math.round(v * 10) / 10;
const round2 = (v: number) => Math.round(v * 100) / 100;

/**
 * Inverse of `worldToZonePercent` for the addon's 0-1 map coordinates
 * (`C_Map.GetPlayerMapPosition`): nx runs left->right (west->east), ny top->bottom.
 */
export function uiMapToWorld(b: ZoneBounds, nx: number, ny: number): SpawnLocation {
  return {
    map: b.mapId,
    x: round2(b.maxX - ny * (b.maxX - b.minX)),
    y: round2(b.maxY - nx * (b.maxY - b.minY)),
  };
}

/** Percent position (0-100, x left->right, y top->bottom) of a world point on the zone map. */
export function worldToZonePercent(b: ZoneBounds, p: WorldPoint): { x: number; y: number } {
  return {
    x: round1(((b.maxY - p.y) / (b.maxY - b.minY)) * 100),
    y: round1(((b.maxX - p.x) / (b.maxX - b.minX)) * 100),
  };
}

/** True when the spawn is on the zone's map and inside its box (edges inclusive). */
export function boundsContain(b: ZoneBounds, loc: SpawnLocation): boolean {
  return (
    loc.map === b.mapId &&
    loc.x >= b.minX &&
    loc.x <= b.maxX &&
    loc.y >= b.minY &&
    loc.y <= b.maxY
  );
}

const area = (b: ZoneBounds) => (b.maxX - b.minX) * (b.maxY - b.minY);

/**
 * The zone most spawns fall in. Nested boxes (a city inside its zone) resolve to the smallest
 * enclosing one; spawns on maps without a zone map (instances) cast no vote.
 */
export function inferZone(
  locations: SpawnLocation[],
  zones: ZoneBounds[] = ZONE_BOUNDS.zones,
): string | undefined {
  const votes = new Map<string, number>();
  for (const loc of locations) {
    let best: ZoneBounds | undefined;
    for (const z of zones) {
      if (boundsContain(z, loc) && (!best || area(z) < area(best))) best = z;
    }
    if (best) votes.set(best.name, (votes.get(best.name) ?? 0) + 1);
  }
  let winner: string | undefined;
  let max = 0;
  for (const [name, n] of votes) {
    if (n > max) {
      max = n;
      winner = name;
    }
  }
  return winner;
}
